/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import lexicalComposerContext from "@lexical/react/LexicalComposerContext";
import type { MenuTextMatch } from "@lexical/react/LexicalTypeaheadMenuPlugin";
import lexicalTypeaheadMenuPlugin from "@lexical/react/LexicalTypeaheadMenuPlugin";
import type { TextNode } from "lexical";
import { Avatar } from "prism-react/components/Avatar";
import { Box } from "prism-react/components/Box";
import { Stack } from "prism-react/components/Stack";
import { Text } from "prism-react/components/Text/Text";
import { styled } from "prism-react/theme";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as ReactDOM from "react-dom";

import { item } from "../../../css/menu";
import { $createMentionNode } from "../nodes/MentionNode";

const { useLexicalComposerContext } = lexicalComposerContext;
const {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} = lexicalTypeaheadMenuPlugin;
const PUNCTUATION =
  "\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%'\"~=<>_:;";
const NAME = "\\b[A-Z][^\\s" + PUNCTUATION + "]";

const DocumentMentionsRegex = {
  NAME,
  PUNCTUATION,
};

const CapitalizedNameMentionsRegex = new RegExp(
  "(^|[^#])((?:" + DocumentMentionsRegex.NAME + "{" + 1 + ",})$)",
);

const PUNC = DocumentMentionsRegex.PUNCTUATION;

const TRIGGERS = ["@"].join("");

// Chars we expect to see in a mention (non-space, non-punctuation).
const VALID_CHARS = "[^" + TRIGGERS + PUNC + "\\s]";

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
  "(?:" +
  "\\.[ |$]|" + // E.g. "r. " in "Mr. Smith"
  " |" + // E.g. " " in "Josh Duck"
  "[" +
  PUNC +
  "]|" + // E.g. "-' in "Salier-Hellendag"
  ")";

const LENGTH_LIMIT = 75;

const AtSignMentionsRegex = new RegExp(
  "(^|\\s|\\()(" +
    "[" +
    TRIGGERS +
    "]" +
    "((?:" +
    VALID_CHARS +
    VALID_JOINS +
    "){0," +
    LENGTH_LIMIT +
    "})" +
    ")$",
);

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50;

// Regex used to match alias.
const AtSignMentionsRegexAliasRegex = new RegExp(
  "(^|\\s|\\()(" +
    "[" +
    TRIGGERS +
    "]" +
    "((?:" +
    VALID_CHARS +
    "){0," +
    ALIAS_LENGTH_LIMIT +
    "})" +
    ")$",
);

// At most, 5 suggestions are shown in the popup.
const SUGGESTION_LIST_LENGTH_LIMIT = 5;

type MentionUser = { name: string; username: string; avatarUrl?: string };
export type MentionUsers = MentionUser[];

function useMentionLookupService(
  mentionString: string | null,
  users: MentionUsers,
) {
  const [results, setResults] = useState<MentionUsers>([]);

  const search = async () => {
    if (mentionString == null) {
      setResults([]);
      return;
    }

    const results = users.filter(
      (mention) =>
        mention.name.toLowerCase().includes(mentionString.toLowerCase()) ||
        mention.username.toLowerCase().includes(mentionString.toLowerCase()),
    );

    setResults(results);
  };

  useEffect(() => {
    search();
  }, [mentionString]);

  return results;
}

function checkForCapitalizedNameMentions(
  text: string,
  minMatchLength: number,
): MenuTextMatch | null {
  const match = CapitalizedNameMentionsRegex.exec(text);
  if (match !== null) {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    const maybeLeadingWhitespace = match[1]!;

    const matchingString = match[2];
    if (matchingString != null && matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + maybeLeadingWhitespace.length,
        matchingString,
        replaceableString: matchingString,
      };
    }
  }
  return null;
}

function checkForAtSignMentions(
  text: string,
  minMatchLength: number,
): MenuTextMatch | null {
  let match = AtSignMentionsRegex.exec(text);

  if (match === null) {
    match = AtSignMentionsRegexAliasRegex.exec(text);
  }
  if (match !== null) {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    const maybeLeadingWhitespace = match[1]!;

    const matchingString = match[3]!;
    if (matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + maybeLeadingWhitespace.length,
        matchingString,
        replaceableString: match[2]!,
      };
    }
  }
  return null;
}

function getPossibleQueryMatch(text: string): MenuTextMatch | null {
  const match = checkForAtSignMentions(text, 1);
  return match === null ? checkForCapitalizedNameMentions(text, 3) : match;
}

class MentionTypeaheadOption extends MenuOption {
  user: MentionUser;

  constructor(user: MentionUser) {
    super(user.name);
    this.user = user;
  }
}

function MentionsTypeaheadMenuItem({
  index,
  selected,
  onClick,
  onMouseEnter,
  option,
}: {
  index: number;
  selected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: MentionTypeaheadOption;
}) {
  return (
    <StyledTypeaheadItem
      key={option.key}
      ref={option.setRefElement}
      id={"typeahead-item-" + index}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      role="option"
      selected={selected}
      tabIndex={-1}
    >
      <Stack horizontal>
        <Box>
          <Avatar
            name={option.user.name}
            shape="circle"
            src={option.user.avatarUrl}
          />
        </Box>
        <Stack gap={1}>
          <Text>{option.user.name}</Text>
          <Text as="span" color="neutral-fg-base" size="xs">
            @{option.user.username}
          </Text>
        </Stack>
      </Stack>
    </StyledTypeaheadItem>
  );
}

export const MentionsPlugin = ({
  users,
}: {
  users: MentionUsers;
}): JSX.Element | null => {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState<string | null>(null);
  const results = useMentionLookupService(queryString, users);

  const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch("/", {
    minLength: 0,
  });

  const options = useMemo(
    () =>
      results
        .map((result) => new MentionTypeaheadOption(result))
        .slice(0, SUGGESTION_LIST_LENGTH_LIMIT),
    [results],
  );

  const onSelectOption = useCallback(
    (
      selectedOption: MentionTypeaheadOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void,
    ) => {
      editor.update(() => {
        const mentionNode = $createMentionNode(selectedOption.user.username);
        if (nodeToReplace) {
          nodeToReplace.replace(mentionNode);
        }
        mentionNode.select();
        closeMenu();
      });
    },
    [editor],
  );

  const checkForMentionMatch = useCallback(
    (text: string) => {
      const slashMatch = checkForSlashTriggerMatch(text, editor);
      if (slashMatch !== null) {
        return null;
      }
      return getPossibleQueryMatch(text);
    },
    [checkForSlashTriggerMatch, editor],
  );

  return (
    <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex },
      ) =>
        anchorElementRef.current && results.length
          ? ReactDOM.createPortal(
              <StyledTypeaheadContent>
                {options.map((option, i: number) => (
                  <MentionsTypeaheadMenuItem
                    key={option.key}
                    index={i}
                    onClick={() => {
                      setHighlightedIndex(i);
                      selectOptionAndCleanUp(option);
                    }}
                    onMouseEnter={() => {
                      setHighlightedIndex(i);
                    }}
                    option={option}
                    selected={selectedIndex === i}
                  />
                ))}
              </StyledTypeaheadContent>,
              anchorElementRef.current,
            )
          : null
      }
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      options={options}
      triggerFn={checkForMentionMatch}
    />
  );
};

const StyledTypeaheadContent = styled("div", {
  background: "$neutral-bg-subtle",
  boxShadow: "$popup",
  cursor: "var(--csb-cursor)",
  padding: "$1 0",
  minWidth: "180px",
  borderRadius: "$2",
});

const StyledTypeaheadItem = styled("div", {
  ...item,
  variants: {
    selected: {
      true: {
        background: "$neutral-bg-focus",
        color: "$neutral-fg-high",
      },
    },
  },
});
